import { useState } from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import classNames from 'classnames';

import lang from '../../../lib/language';

import ClearIcon from '../../../assets/img/close-circle.svg';
import DownArrow from '../../../assets/img/icon_expan_down_ratios.svg';
import { ClickableDiv, ClickableImage, MobileDrawer } from '../../../components';
import { useBrowserWidth, useIsMobile } from '../../../hooks';
import { ZOOM_BEHAVIOR_WIDTH } from '../../../util/constants';
import { useStyles } from '../TradeHistoryStyles';

import styles from './StatusSelector.module.scss';

type Props = {
    currentStatus: string;
    handleChangeByStatus: (status: string) => void;
    statusList: string[];
    handleReset: () => void;
}

const StatusSelector = (props: Props) => {
  const { currentStatus, handleChangeByStatus, statusList, handleReset } = props;
  const { browserWidthSize } = useBrowserWidth();
  const classes = useStyles();
  const { isMobile } = useIsMobile();

  const [isOpenStatusField, setIsOpenStatusField] = useState<boolean>(false);

  const isStatusSelected = currentStatus !== lang.commonOrderStatusFilterTitle();

  const handleOnOpen = () => {
    setIsOpenStatusField(true);
  };

  const handleToggleOpen = () => {
    setIsOpenStatusField((open) => !open);
  };

  const handleOnClose = () => {
    setIsOpenStatusField(false);
  };

  const handleChange = (status: string) => {
    handleChangeByStatus(status);
    setIsOpenStatusField(false);
  };

  const onButtonClose = (event) => {
    event.stopPropagation();
    handleReset();
  };

  const StatusPillIcon = ({ className }: { className?: string }) => (
    isStatusSelected ? (
      <ClickableImage
        src={ClearIcon}
        alt="clear"
        className={classNames(className, classes.clearIcon)}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={onButtonClose}
      />
    ) : (
      <img src={DownArrow} alt="" className={classNames(className, classes.chevronIcon)} />
    )
  );

  return (
    <>
      { isMobile ? (
        <>
          <div
            className={classNames(styles.statusWrapper, { [styles.statusWrapperOpen]: isOpenStatusField })}
          >
            <ClickableDiv onClick={handleToggleOpen}>
              <div>
                {currentStatus}
              </div>
            </ClickableDiv>
            {isStatusSelected ? (
              <ClickableImage
                className={styles.clearButton}
                src={ClearIcon}
                alt="clear"
                onClick={onButtonClose}
              />
            ) : (
              <ClickableImage
                className={classNames(styles.chevron, { [styles.chevronOpen]: isOpenStatusField })}
                src={DownArrow}
                alt=""
                onClick={handleToggleOpen}
              />
            )}
          </div>
          <MobileDrawer
            isOpen={isOpenStatusField}
            title={lang.commonOrderStatusFilterTitle()}
            onCloseStart={handleOnClose}
            onClose={handleOnClose}
            usePortal
          >
            {statusList.slice(1).map((status) => (
              <button
                key={status}
                type="button"
                className={classNames(styles.orderType, { [styles.orderTypeSelected]: status === currentStatus })}
                onClick={() => { handleChange(status); }}
              >
                {status}
              </button>
            ))}
          </MobileDrawer>
        </>
      ) : (
        <FormControl classes={{ root: classes.formControl }}>
          <Select
            variant="standard"
            value={currentStatus}
            disableUnderline
            sx={{
              color: '#F2F4F7',
              fontFamily: 'Manrope-Regular',
              outline: 'none',
              '&&&& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                boxSizing: 'border-box',
                padding: '0 36px 0 16px',
              },
              '&&&& .MuiSelect-select:focus': {
                backgroundColor: 'transparent',
                borderRadius: '32px',
              },
            }}
            MenuProps={{
              variant: 'menu',
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              slotProps: {
                backdrop: {
                  sx: {
                    '&.MuiBackdrop-root': {
                      backgroundColor: 'transparent !important',
                    },
                  },
                },
              },
              PaperProps: {
                style: {
                  backgroundColor: '#191C2E',
                  color: '#FFFFFF',
                  borderRadius: 12,
                  marginTop: 8,
                  marginLeft: browserWidthSize <= 400 ? '10%' : '',
                  zoom: browserWidthSize <= ZOOM_BEHAVIOR_WIDTH ? `${Math.floor(browserWidthSize / 10).toString()}%` : '',
                },
              },
            }}
            classes={{
              root: classNames(classes.statusSelect, { [classes.statusSelectOpen]: isOpenStatusField }),
            }}
            onOpen={handleOnOpen}
            onClose={handleOnClose}
            onChange={(event) => handleChange(event.target.value)}
            IconComponent={StatusPillIcon}
          >
            {statusList.map((item) => (
              <MenuItem
                key={item}
                value={item}
                classes={{
                  root: classes.menuItemRoot,
                  selected: classes.selected,
                }}
              >
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </>
  );
};

export default StatusSelector;
